import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, Box, Typography, Paper, Grid, Card, CardContent, CardHeader,
  Button, TextField, FormControl, InputLabel, Select, MenuItem, Rating,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, LinearProgress,
  Tabs, Tab, CircularProgress, Divider, Stack, Avatar, Tooltip, Snackbar
} from '@mui/material';
import {
  Assessment, Add, Edit, Delete, Visibility, Person, Groups,
  TrendingUp, CheckCircle, Schedule, Close, Save, Search
} from '@mui/icons-material';
import { fetchJson } from '../../../utils/fetchJson';
import useScopeAutoRefresh from '../../../hooks/useScopeAutoRefresh';

// Evaluation criteria definitions
const EVALUATION_CATEGORIES = [
  { key: 'professionalKnowledge', label: 'Professional Knowledge', desc: 'Mastery of subject matter and professional expertise' },
  { key: 'teachingEffectiveness', label: 'Teaching / Job Effectiveness', desc: 'Ability to effectively perform core job responsibilities' },
  { key: 'classroomManagement', label: 'Classroom / Workspace Management', desc: 'Organization, discipline, and workspace management skills' },
  { key: 'punctualityAttendance', label: 'Punctuality & Attendance', desc: 'Regularity, punctuality, and adherence to schedules' },
  { key: 'communicationSkills', label: 'Communication Skills', desc: 'Clarity of communication with students, parents, and colleagues' },
  { key: 'teamworkCollaboration', label: 'Teamwork & Collaboration', desc: 'Ability to work cooperatively with colleagues and administration' },
  { key: 'initiativeCreativity', label: 'Initiative & Creativity', desc: 'Willingness to take initiative and bring innovative ideas' },
  { key: 'professionalism', label: 'Professionalism & Ethics', desc: 'Professional conduct, dress code, and ethical standards' },
  { key: 'studentRelations', label: 'Student / Client Relations', desc: 'Quality of interactions with students, parents, or stakeholders' },
  { key: 'administrativeDuties', label: 'Administrative Duties', desc: 'Completion of administrative tasks, reports, and documentation' },
];

const RATING_LABELS = { 1: 'Poor', 2: 'Below Average', 3: 'Satisfactory', 4: 'Good', 5: 'Excellent' };
const PERIODS = ['Term 1', 'Term 2', 'Term 3', 'Annual'];
const AUTO_CATEGORY_KEYS = ['punctualityAttendance', 'teachingEffectiveness', 'administrativeDuties'];

const normalizeRoleKey = (value) => (value || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
const isTeacherEvaluationRole = (role) => ['teacher', 'classmaster', 'subjecthead'].includes(normalizeRoleKey(role));
const clampRatingValue = (value) => Math.max(1, Math.min(5, value));
const toHalfStepRating = (value) => Math.round(clampRatingValue(value) * 2) / 2;
const percentageToRating = (percentage) => {
  const safePercentage = Math.max(0, Math.min(100, Number(percentage) || 0));
  return toHalfStepRating(1 + (safePercentage / 100) * 4);
};

const getTeacherMetricForStaff = (staffMember, teacherMetrics) => {
  if (!staffMember) return null;
  const staffId = String(staffMember.id || '');
  const userId = String(staffMember.userId || '');
  return (teacherMetrics || []).find((metric) => {
    if (staffId && String(metric?.staffId || '') === staffId) return true;
    if (userId && String(metric?.teacherId || '') === userId) return true;
    return staffId && String(metric?.teacherId || '') === staffId;
  }) || null;
};

const buildAutoEvaluationData = (staffMember, teacherMetrics) => {
  if (!staffMember || !isTeacherEvaluationRole(staffMember.role)) {
    return { metric: null, values: {}, reasons: {} };
  }

  const metric = getTeacherMetricForStaff(staffMember, teacherMetrics);
  if (!metric) {
    return { metric: null, values: {}, reasons: {} };
  }

  const attendanceTotal = Number(metric.attendanceSessionsTotal || 0);
  const attendanceSameDay = Number(metric.attendanceSameDaySessions || 0);
  const avgMark = metric.avgMark != null ? Number(metric.avgMark) : null;
  const markCount = Number(metric.markCount || 0);

  const values = {};
  const reasons = {};

  if (attendanceTotal > 0) {
    const sameDayPct = (attendanceSameDay / attendanceTotal) * 100;
    values.punctualityAttendance = percentageToRating(sameDayPct);
    reasons.punctualityAttendance = `${attendanceSameDay}/${attendanceTotal} same-day attendance entries (${sameDayPct.toFixed(0)}%)`;
  }

  if (Number.isFinite(avgMark)) {
    const markPct = Math.max(0, Math.min(100, (avgMark / 20) * 100));
    values.teachingEffectiveness = percentageToRating(markPct);
    reasons.teachingEffectiveness = `Average class mark ${avgMark.toFixed(1)}/20`;
  }

  const administrativeSignals = [];
  const administrativeReasons = [];
  if (attendanceTotal > 0) {
    administrativeSignals.push((attendanceSameDay / attendanceTotal) * 100);
    administrativeReasons.push(`${attendanceTotal} attendance sessions recorded`);
  }
  if (markCount > 0) {
    administrativeSignals.push(Math.min(100, (markCount / 60) * 100));
    administrativeReasons.push(`${markCount} marks recorded`);
  }
  if (administrativeSignals.length > 0) {
    const administrativePct = administrativeSignals.reduce((sum, value) => sum + value, 0) / administrativeSignals.length;
    values.administrativeDuties = percentageToRating(administrativePct);
    reasons.administrativeDuties = administrativeReasons.join(' • ');
  }

  return { metric, values, reasons };
};

const ratingColor = (r) => {
  if (r >= 4.5) return 'success';
  if (r >= 3.5) return 'primary';
  if (r >= 2.5) return 'warning';
  return 'error';
};

const defaultFormData = () => {
  const obj = { staffId: '', period: '', evaluationDate: new Date().toISOString().slice(0, 10), strengths: '', areasForImprovement: '', goals: '', comments: '', status: 'draft' };
  EVALUATION_CATEGORIES.forEach(c => { obj[c.key] = 0; });
  return obj;
};

const StaffEvaluationPage = () => {
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [teacherMetrics, setTeacherMetrics] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // create | edit | view
  const [formData, setFormData] = useState(defaultFormData());
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);

  // Filter state
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchText, setSearchText] = useState('');

  const [terms, setTerms] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchJson('/api/terms').catch(() => []);
        if (cancelled) return;
        setTerms(Array.isArray(list) ? list : []);
      } catch (_) {
        if (!cancelled) setTerms([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const termWindows = useMemo(() => {
    const list = (terms || [])
      .map((t) => {
        const s = String(t?.startDate || '').slice(0, 10);
        const e = String(t?.endDate || '').slice(0, 10);
        return { id: t?.id, startDate: s, endDate: e };
      })
      .filter((t) => t.startDate && t.endDate)
      .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

    // Force exactly 3 term labels; if fewer exist, we'll still show what we have.
    return list.slice(0, 3).map((t, idx) => ({
      period: `Term ${idx + 1}`,
      startDate: t.startDate,
      endDate: t.endDate
    }));
  }, [terms]);

  const periodOptions = useMemo(() => {
    const termsOpts = termWindows.map((tw) => ({
      period: tw.period,
      label: `${tw.period} — ${tw.startDate} → ${tw.endDate}`,
      startDate: tw.startDate,
      endDate: tw.endDate
    }));
    return [...termsOpts, { period: 'Annual', label: 'Annual', startDate: null, endDate: null }];
  }, [termWindows]);

  // Automatic evaluation: scorecards computed server-side from each staff
  // member's own recorded activity, plus the per-role leaderboard. Same
  // endpoint the mobile app uses, so the two clients cannot disagree.
  const [autoData, setAutoData] = useState(null);
  const [autoBusy, setAutoBusy] = useState(false);
  const [overrideTarget, setOverrideTarget] = useState(null);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [staffRes, evalsRes, statsRes, metricsRes, autoRes] = await Promise.allSettled([
        fetchJson('/api/staff-management/evaluations/staff-list'),
        fetchJson('/api/staff-management/evaluations'),
        fetchJson('/api/staff-management/evaluations/dashboard'),
        fetchJson('/api/school-admin/teacher-evaluation-metrics'),
        fetchJson('/api/staff-management/evaluations/auto')
      ]);
      setStaff(staffRes.status === 'fulfilled' ? (staffRes.value?.staff || []) : []);
      setEvaluations(evalsRes.status === 'fulfilled' ? (evalsRes.value?.evaluations || []) : []);
      setDashboardStats(statsRes.status === 'fulfilled' ? statsRes.value : null);
      setTeacherMetrics(metricsRes.status === 'fulfilled' ? (metricsRes.value?.metrics || []) : []);
      setAutoData(autoRes.status === 'fulfilled' ? autoRes.value : null);

      const failed = [staffRes, evalsRes, statsRes, metricsRes].filter(r => r.status === 'rejected');
      if (failed.length) {
        const firstErr = failed[0]?.reason;
        setSnackbar({ open: true, message: firstErr?.message || 'Some evaluation data failed to load', severity: 'warning' });
      }
    } catch (error) {
      console.error('Failed to fetch evaluation data:', error);
      setSnackbar({ open: true, message: error?.message || 'Failed to fetch evaluation data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useScopeAutoRefresh([fetchData]);

  const openCreateDialog = (staffMember) => {
    const fd = defaultFormData();
    if (staffMember) fd.staffId = staffMember.id;
    setFormData(fd);
    setDialogMode('create');
    setEditId(null);
    setDialogOpen(true);
  };

  const openEditDialog = (evaluation) => {
    const fd = { ...defaultFormData() };
    EVALUATION_CATEGORIES.forEach(c => { fd[c.key] = evaluation[c.key] || 0; });
    fd.staffId = evaluation.staffId;
    fd.period = evaluation.period || '';
    fd.evaluationDate = evaluation.evaluationDate || new Date().toISOString().slice(0, 10);
    fd.strengths = evaluation.strengths || '';
    fd.areasForImprovement = evaluation.areasForImprovement || '';
    fd.goals = evaluation.goals || '';
    fd.comments = evaluation.comments || '';
    fd.status = evaluation.status || 'draft';
    setFormData(fd);
    setDialogMode('edit');
    setEditId(evaluation.id);
    setDialogOpen(true);
  };

  const openViewDialog = (evaluation) => {
    const fd = { ...defaultFormData() };
    EVALUATION_CATEGORIES.forEach(c => { fd[c.key] = evaluation[c.key] || 0; });
    fd.staffId = evaluation.staffId;
    fd.period = evaluation.period || '';
    fd.evaluationDate = evaluation.evaluationDate || '';
    fd.strengths = evaluation.strengths || '';
    fd.areasForImprovement = evaluation.areasForImprovement || '';
    fd.goals = evaluation.goals || '';
    fd.comments = evaluation.comments || '';
    fd.status = evaluation.status || 'draft';
    setFormData(fd);
    setDialogMode('view');
    setEditId(evaluation.id);
    setDialogOpen(true);
  };

  const handleSave = async (submitStatus) => {
    if (!formData.staffId) { setSnackbar({ open: true, message: 'Please select a staff member', severity: 'warning' }); return; }
    const hasAnyRating = EVALUATION_CATEGORIES.some(c => formData[c.key] > 0);
    if (!hasAnyRating) { setSnackbar({ open: true, message: 'Please rate at least one category', severity: 'warning' }); return; }
    try {
      setSaving(true);
      const payload = { ...formData, status: submitStatus || formData.status };
      if (dialogMode === 'edit' && editId) {
        await fetchJson(`/api/staff-management/evaluations/${editId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        setSnackbar({ open: true, message: 'Evaluation updated successfully', severity: 'success' });
      } else {
        await fetchJson('/api/staff-management/evaluations', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        setSnackbar({ open: true, message: 'Evaluation created successfully', severity: 'success' });
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to save evaluation', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await fetchJson(`/api/staff-management/evaluations/${deleteTarget}`, { method: 'DELETE' });
      setSnackbar({ open: true, message: 'Evaluation deleted', severity: 'success' });
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to delete', severity: 'error' });
    }
  };

  // Computed: unique roles from staff
  const roles = useMemo(() => {
    const r = new Set(staff.map(s => s.role).filter(Boolean));
    return Array.from(r).sort();
  }, [staff]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress size={56} /></Box>;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assessment color="primary" /> Staff Evaluation
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Evaluate and track staff performance across all departments
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => openCreateDialog(null)}>
          New Evaluation
        </Button>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={selectedTab} onChange={(_, v) => setSelectedTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Dashboard" icon={<Assessment />} iconPosition="start" />
          <Tab label="Evaluations" icon={<Groups />} iconPosition="start" />
          <Tab label="Evaluate Staff" icon={<Edit />} iconPosition="start" />
          <Tab label="Teacher Metrics" icon={<TrendingUp />} iconPosition="start" />
          <Tab label="Automatic" icon={<Assessment />} iconPosition="start" />
        </Tabs>
      </Paper>

      {selectedTab === 0 && <DashboardTab stats={dashboardStats} evaluations={evaluations} staff={staff} onEvaluate={openCreateDialog} />}
      {selectedTab === 1 && (
        <EvaluationsTab
          evaluations={evaluations} staff={staff}
          filterRole={filterRole} setFilterRole={setFilterRole}
          filterStatus={filterStatus} setFilterStatus={setFilterStatus}
          searchText={searchText} setSearchText={setSearchText}
          roles={roles}
          onView={openViewDialog} onEdit={openEditDialog}
          onDelete={(id) => { setDeleteTarget(id); setDeleteDialogOpen(true); }}
        />
      )}
      {selectedTab === 2 && <EvaluateStaffTab staff={staff} roles={roles} onEvaluate={openCreateDialog} evaluations={evaluations} />}
      {selectedTab === 3 && <TeacherMetricsTab metrics={teacherMetrics} />}
      {selectedTab === 4 && (
        <AutoEvaluationTab
          data={autoData}
          busy={autoBusy}
          onOverride={(card, criterion) => setOverrideTarget({
            card,
            criterion,
            score: criterion.score == null ? '' : String(criterion.score),
            note: '',
            clear: false,
          })}
        />
      )}

      {/* Override one criterion by hand.

          This is the half the system cannot do: professional knowledge,
          teamwork and initiative have no honest signal, and any measured
          criterion may be wrong about a particular person in a particular term.
          Clearing an override hands the criterion back to the engine. */}
      <Dialog open={Boolean(overrideTarget)} onClose={() => !autoBusy && setOverrideTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{overrideTarget?.criterion?.label}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {overrideTarget?.card?.name} — {overrideTarget?.criterion?.reason}
          </Typography>
          <TextField
            fullWidth type="number" label="Score out of 5" sx={{ mb: 2 }}
            inputProps={{ min: 0, max: 5, step: 0.5 }}
            value={overrideTarget?.score ?? ''}
            onChange={(e) => setOverrideTarget((t) => ({ ...t, score: e.target.value, clear: false }))}
          />
          <TextField
            fullWidth multiline rows={2} label="Why (optional)"
            value={overrideTarget?.note ?? ''}
            onChange={(e) => setOverrideTarget((t) => ({ ...t, note: e.target.value }))}
          />
          {overrideTarget?.criterion?.source === 'overridden' && (
            <Button sx={{ mt: 2 }} onClick={() => setOverrideTarget((t) => ({ ...t, clear: true, score: '' }))}>
              Clear and use the measured score
            </Button>
          )}
          {overrideTarget?.clear && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Saving now returns this criterion to the system's own score.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideTarget(null)} disabled={autoBusy}>Cancel</Button>
          <Button
            variant="contained" disabled={autoBusy || (!overrideTarget?.clear && !String(overrideTarget?.score || '').trim())}
            onClick={async () => {
              setAutoBusy(true);
              try {
                await fetchJson(`/api/staff-management/evaluations/auto/${overrideTarget.card.staffId}/override`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: {
                    criterion: overrideTarget.criterion.key,
                    score: overrideTarget.clear ? null : Number(overrideTarget.score),
                    note: overrideTarget.note || null,
                  },
                });
                setOverrideTarget(null);
                setSnackbar({ open: true, message: overrideTarget.clear ? 'Returned to the system’s own score.' : 'Score saved.', severity: 'success' });
                const fresh = await fetchJson('/api/staff-management/evaluations/auto').catch(() => null);
                if (fresh) setAutoData(fresh);
              } catch (err) {
                setSnackbar({ open: true, message: err?.message || 'Could not save that score.', severity: 'error' });
              } finally {
                setAutoBusy(false);
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Evaluation Form Dialog */}
      <EvaluationDialog
        open={dialogOpen} onClose={() => setDialogOpen(false)}
        mode={dialogMode} formData={formData} setFormData={setFormData}
        staff={staff} saving={saving} onSave={handleSave}
        teacherMetrics={teacherMetrics}
        periodOptions={periodOptions}
        termWindows={termWindows}
      />

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Evaluation</DialogTitle>
        <DialogContent><Typography>Are you sure you want to delete this evaluation? This action cannot be undone.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
};

// ─── Dashboard Tab ────────────────────────────────────────────
const DashboardTab = ({ stats, evaluations, staff, onEvaluate }) => {
  const s = stats || {};
  const recentEvals = (evaluations || []).slice(0, 5);

  return (
    <Grid container spacing={3}>
      <Grid item xs={6} sm={3}>
        <Card sx={{ textAlign: 'center' }}>
          <CardContent>
            <Person sx={{ fontSize: 36, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4">{s.totalStaff || staff.length || 0}</Typography>
            <Typography variant="body2" color="text.secondary">Total Staff</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={6} sm={3}>
        <Card sx={{ textAlign: 'center' }}>
          <CardContent>
            <CheckCircle sx={{ fontSize: 36, color: 'success.main', mb: 1 }} />
            <Typography variant="h4" color="success.main">{s.evaluatedStaff || 0}</Typography>
            <Typography variant="body2" color="text.secondary">Evaluated</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={6} sm={3}>
        <Card sx={{ textAlign: 'center' }}>
          <CardContent>
            <Schedule sx={{ fontSize: 36, color: 'warning.main', mb: 1 }} />
            <Typography variant="h4" color="warning.main">{s.pendingEvaluations || 0}</Typography>
            <Typography variant="body2" color="text.secondary">Pending</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={6} sm={3}>
        <Card sx={{ textAlign: 'center' }}>
          <CardContent>
            <TrendingUp sx={{ fontSize: 36, color: 'info.main', mb: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
              <Typography variant="h4">{(s.averageRating || 0).toFixed(1)}</Typography>
              <Rating value={s.averageRating || 0} readOnly size="small" precision={0.1} />
            </Box>
            <Typography variant="body2" color="text.secondary">Avg Rating</Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Rating Distribution */}
      {s.ratingDistribution && (
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Rating Distribution" />
            <CardContent>
              {[
                { label: 'Excellent (4.5-5)', count: s.ratingDistribution.excellent || 0, color: '#4caf50' },
                { label: 'Good (3.5-4.4)', count: s.ratingDistribution.good || 0, color: '#2196f3' },
                { label: 'Satisfactory (2.5-3.4)', count: s.ratingDistribution.satisfactory || 0, color: '#ff9800' },
                { label: 'Needs Improvement (1.5-2.4)', count: s.ratingDistribution.needsImprovement || 0, color: '#f44336' },
                { label: 'Poor (< 1.5)', count: s.ratingDistribution.poor || 0, color: '#9e9e9e' },
              ].map(d => {
                const total = s.submittedEvaluations || 1;
                const pct = total > 0 ? (d.count / total) * 100 : 0;
                return (
                  <Box key={d.label} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">{d.label}</Typography>
                      <Typography variant="body2" fontWeight="bold">{d.count}</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 4, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: d.color, borderRadius: 4 } }} />
                  </Box>
                );
              })}
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Recent Evaluations */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title="Recent Evaluations" />
          <CardContent>
            {recentEvals.length === 0 ? (
              <Alert severity="info">No evaluations recorded yet. Start by evaluating a staff member.</Alert>
            ) : (
              <Stack spacing={1.5}>
                {recentEvals.map(ev => {
                  const staffName = ev.staff?.user ? (ev.staff.user.name || '') : '';
                  const safeStaffName = staffName || `Staff #${ev.staffId}`;
                  const roleName = ev.staff?.role?.name || '';
                  return (
                    <Box key={ev.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, borderRadius: 1, bgcolor: 'grey.50' }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontSize: 14 }}>{safeStaffName.charAt(0)}</Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight="bold" noWrap>{safeStaffName}</Typography>
                        <Typography variant="caption" color="text.secondary">{roleName} {ev.period ? `• ${ev.period}` : ''}</Typography>
                      </Box>
                      <Rating value={ev.overallRating || 0} readOnly size="small" precision={0.1} />
                      <Chip label={ev.status} size="small" color={ev.status === 'submitted' ? 'success' : ev.status === 'acknowledged' ? 'info' : 'default'} />
                    </Box>
                  );
                })}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Quick Evaluate */}
      <Grid item xs={12}>
        <Card>
          <CardHeader title="Staff Quick View" subheader="Select a staff member to start a new evaluation" />
          <CardContent>
            {staff.length === 0 ? (
              <Alert severity="info">No staff members found in the current context.</Alert>
            ) : (
              <Grid container spacing={2}>
                {staff.slice(0, 12).map(s => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={s.id}>
                    <Card variant="outlined" sx={{ cursor: 'pointer', '&:hover': { borderColor: 'primary.main', boxShadow: 1 } }} onClick={() => onEvaluate(s)}>
                      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
                        <Avatar sx={{ bgcolor: 'primary.light', width: 40, height: 40 }}>{(s.name || '?').charAt(0)}</Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight="bold" noWrap>{s.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{s.role} {s.department ? `• ${s.department}` : ''}</Typography>
                        </Box>
                        <Add color="primary" fontSize="small" />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// ─── Evaluations Tab (List) ────────────────────────────────────
const EvaluationsTab = ({ evaluations, staff, filterRole, setFilterRole, filterStatus, setFilterStatus, searchText, setSearchText, roles, onView, onEdit, onDelete }) => {
  const filtered = useMemo(() => {
    return (evaluations || []).filter(ev => {
      const staffInfo = ev.staff || {};
      const userName = staffInfo.user ? String(staffInfo.user.name || '').toLowerCase() : '';
      const roleName = (staffInfo.role?.name || '').toLowerCase();
      if (filterRole !== 'all' && roleName !== filterRole.toLowerCase()) return false;
      if (filterStatus !== 'all' && ev.status !== filterStatus) return false;
      if (searchText && !userName.includes(searchText.toLowerCase())) return false;
      return true;
    });
  }, [evaluations, filterRole, filterStatus, searchText]);

  return (
    <Card>
      <CardHeader
        title={`Evaluations (${filtered.length})`}
        action={
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Search staff..." value={searchText} onChange={e => setSearchText(e.target.value)}
              InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} /> }} sx={{ width: 180 }} />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Role</InputLabel>
              <Select value={filterRole} label="Role" onChange={e => setFilterRole(e.target.value)}>
                <MenuItem value="all">All Roles</MenuItem>
                {roles.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="submitted">Submitted</MenuItem>
                <MenuItem value="acknowledged">Acknowledged</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        }
      />
      <CardContent sx={{ p: 0 }}>
        {filtered.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No evaluations found matching the current filters.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Staff Member</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="center">Rating</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Evaluator</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(ev => {
                  const u = ev.staff?.user || {};
                  const name = u.name || `#${ev.staffId}`;
                  const role = ev.staff?.role?.name || '—';
                  const evaluator = ev.evaluator ? (ev.evaluator.name || '') : '';
                  const safeEvaluator = evaluator || '—';
                  return (
                    <TableRow key={ev.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'primary.light' }}>{name.charAt(0)}</Avatar>
                          <Typography variant="body2">{name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell><Chip label={role} size="small" variant="outlined" /></TableCell>
                      <TableCell>{ev.period || '—'}</TableCell>
                      <TableCell>{ev.evaluationDate || '—'}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <Typography variant="body2" fontWeight="bold">{(ev.overallRating || 0).toFixed(1)}</Typography>
                          <Rating value={ev.overallRating || 0} readOnly size="small" precision={0.1} />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={ev.status} size="small" color={ev.status === 'submitted' ? 'success' : ev.status === 'acknowledged' ? 'info' : 'default'} />
                      </TableCell>
                      <TableCell><Typography variant="caption">{safeEvaluator}</Typography></TableCell>
                      <TableCell align="right">
                        <Tooltip title="View"><IconButton size="small" onClick={() => onView(ev)}><Visibility fontSize="small" /></IconButton></Tooltip>
                        {ev.status === 'draft' && <Tooltip title="Edit"><IconButton size="small" onClick={() => onEdit(ev)}><Edit fontSize="small" /></IconButton></Tooltip>}
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => onDelete(ev.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Evaluate Staff Tab ────────────────────────────────────────
const EvaluateStaffTab = ({ staff, roles, onEvaluate, evaluations }) => {
  const [roleFilter, setRoleFilter] = useState('all');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    return staff.filter(s => {
      if (roleFilter !== 'all' && (s.role || '').toLowerCase() !== roleFilter.toLowerCase()) return false;
      if (search && !(s.name || '').toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [staff, roleFilter, search]);

  // Map staffId -> latest evaluation
  const latestEvalMap = useMemo(() => {
    const map = {};
    (evaluations || []).forEach(ev => {
      if (!map[ev.staffId] || new Date(ev.evaluationDate) > new Date(map[ev.staffId].evaluationDate)) {
        map[ev.staffId] = ev;
      }
    });
    return map;
  }, [evaluations]);

  return (
    <Card>
      <CardHeader
        title="Select Staff to Evaluate"
        subheader="Choose a staff member from the list below to start or continue their evaluation"
        action={
          <Stack direction="row" spacing={1}>
            <TextField size="small" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} /> }} sx={{ width: 180 }} />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Role</InputLabel>
              <Select value={roleFilter} label="Role" onChange={e => setRoleFilter(e.target.value)}>
                <MenuItem value="all">All Roles</MenuItem>
                {roles.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        }
      />
      <CardContent>
        {filtered.length === 0 ? (
          <Alert severity="info">No staff members found.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Staff Member</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Staff No.</TableCell>
                  <TableCell>Last Evaluation</TableCell>
                  <TableCell>Last Rating</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(s => {
                  const lastEval = latestEvalMap[s.id];
                  return (
                    <TableRow key={s.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, fontSize: 14, bgcolor: 'primary.light' }}>{(s.name || '?').charAt(0)}</Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">{s.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{s.email}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell><Chip label={s.role} size="small" variant="outlined" /></TableCell>
                      <TableCell>{s.department || '—'}</TableCell>
                      <TableCell>{s.staffNumber || '—'}</TableCell>
                      <TableCell>
                        {lastEval ? (
                          <Typography variant="caption">{lastEval.evaluationDate} ({lastEval.period || 'N/A'})</Typography>
                        ) : (
                          <Chip label="Not evaluated" size="small" color="warning" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        {lastEval ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" fontWeight="bold">{(lastEval.overallRating || 0).toFixed(1)}</Typography>
                            <Rating value={lastEval.overallRating || 0} readOnly size="small" precision={0.1} />
                          </Box>
                        ) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="contained" startIcon={<Add />} onClick={() => onEvaluate(s)}>
                          Evaluate
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Teacher Metrics Tab ──────────────────────────────────────
// ─── Automatic Evaluation Tab ──────────────────────────────────
//
// The same scorecards the mobile app shows, from the same endpoint, so the two
// clients cannot disagree about what a staff member scored.
//
// The rule this component exists to respect: a null score is NEVER rendered as
// 0. It shows an em dash and its reason, and gets no progress bar — a bar
// pinned to the left edge reads as zero, which is the one thing "not enough
// data" must not look like. At a school that has not started taking registers,
// that is currently every teacher.
//
// Leadership is excluded from the leaderboard server-side: a head does not
// compete with the staff they appraise.
const AutoEvaluationTab = ({ data, onOverride, busy }) => {
  const [expanded, setExpanded] = useState(null);
  const board = data?.topByRole || [];
  const cards = data?.scorecards || [];

  const scoreText = (s) => (s == null ? '—' : Number(s).toFixed(2));
  const toneFor = (s) => {
    if (s == null) return 'text.secondary';
    if (s >= 4) return 'success.main';
    if (s >= 2.5) return 'warning.main';
    return 'error.main';
  };
  const sourceChip = (src) => {
    if (src === 'auto') return { label: 'Measured', color: 'primary' };
    if (src === 'overridden') return { label: 'Corrected', color: 'warning' };
    return { label: 'Needs a person', color: 'default' };
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Scores are computed from what the system recorded for each staff member in{' '}
        <strong>{data?.academicYear || 'this academic year'}</strong>, and are for that year alone.
        A criterion with no evidence reads <strong>—</strong> (not enough data) and is left out of
        the overall — it is never counted as zero. Click any criterion to correct it by hand.
      </Alert>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUp color="primary" /> Highest rated in each role
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            School Admin, Principal and Head Master are not ranked — a head does not compete with
            the staff they appraise.
          </Typography>
          {board.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No staff member has enough recorded activity to be scored this year.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Role</TableCell>
                    <TableCell>Leader</TableCell>
                    <TableCell align="right">Score</TableCell>
                    <TableCell>Scored</TableCell>
                    <TableCell>Ranking</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {board.map((r) => (
                    <TableRow key={r.role}>
                      <TableCell><strong>{r.role}</strong></TableCell>
                      <TableCell>
                        {r.leaders.length === 0
                          ? <Typography variant="body2" color="text.secondary">None scorable yet</Typography>
                          : r.leaders.map((l) => l.name).join(', ')}
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ color: toneFor(r.leaders[0]?.score ?? null), fontWeight: 600 }}>
                          {scoreText(r.leaders[0]?.score ?? null)}
                        </Typography>
                      </TableCell>
                      <TableCell>{r.rated} of {r.total}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {r.ranked.map((x) => `${x.name} ${Number(x.score).toFixed(2)}`).join('  ·  ') || '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>All staff</Typography>
          {cards.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No staff member is on record for this phase.
            </Typography>
          ) : cards.map((c) => (
            <Box key={c.staffId} sx={{ borderBottom: 1, borderColor: 'divider', py: 1.5 }}>
              <Box
                onClick={() => setExpanded(expanded === c.staffId ? null : c.staffId)}
                sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1">{c.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {[c.role, c.department].filter(Boolean).join(' · ') || '—'}
                    {' — '}
                    {c.overall.score == null
                      ? 'nothing could be scored yet'
                      : `from ${c.overall.counted} of ${c.overall.of} criteria`}
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ color: toneFor(c.overall.score) }}>
                  {scoreText(c.overall.score)}
                </Typography>
              </Box>

              {expanded === c.staffId && (
                <Box sx={{ mt: 2, pl: { xs: 0, sm: 2 } }}>
                  {c.criteria.map((cr) => (
                    <Box
                      key={cr.key}
                      onClick={() => onOverride(c, cr)}
                      sx={{ py: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, px: 1, borderRadius: 1 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>{cr.label}</Typography>
                        <Chip size="small" {...sourceChip(cr.source)} />
                        {cr.weight > 0 && <Chip size="small" variant="outlined" label={`Weight ${cr.weight}`} />}
                        <Typography sx={{ color: toneFor(cr.score), fontWeight: 600, minWidth: 48, textAlign: 'right' }}>
                          {scoreText(cr.score)}
                        </Typography>
                      </Box>
                      {/* No bar for a null score — a bar at the left edge reads as zero. */}
                      {cr.score != null && (
                        <LinearProgress
                          variant="determinate"
                          value={Math.max(0, Math.min(100, (cr.score / 5) * 100))}
                          sx={{ height: 4, borderRadius: 2, my: 0.5 }}
                        />
                      )}
                      <Typography variant="caption" color="text.secondary" display="block">
                        {cr.reason}
                      </Typography>
                      {cr.computedScore != null && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          The system measured {Number(cr.computedScore).toFixed(2)} before this correction.
                        </Typography>
                      )}
                    </Box>
                  ))}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {c.overall.note}
                  </Typography>
                </Box>
              )}
            </Box>
          ))}
        </CardContent>
      </Card>
    </Box>
  );
};

const TeacherMetricsTab = ({ metrics }) => {
  const hasData = Array.isArray(metrics) && metrics.length > 0;

  return (
    <Card>
      <CardHeader title="Teacher Performance Metrics" subheader="Automated metrics based on attendance recording and student grading activity" />
      <CardContent>
        {!hasData ? (
          <Alert severity="info">No teacher performance metrics available for the current academic context.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Teacher</TableCell>
                  <TableCell align="right">Sessions Recorded</TableCell>
                  <TableCell align="right">Same-day</TableCell>
                  <TableCell align="right">Late</TableCell>
                  <TableCell align="center">Timeliness</TableCell>
                  <TableCell align="right">Avg Mark</TableCell>
                  <TableCell align="right">Marks Recorded</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {metrics.map(m => {
                  const total = Number(m.attendanceSessionsTotal || 0);
                  const sameDay = Number(m.attendanceSameDaySessions || 0);
                  const late = Number(m.attendanceLateSessions || 0);
                  const pct = total > 0 ? (sameDay / total) * 100 : 0;
                  const avgMark = typeof m.avgMark === 'number' ? m.avgMark : null;
                  return (
                    <TableRow key={m.teacherId || m.staffId} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'primary.light' }}>{(m.name || '?').charAt(0)}</Avatar>
                          <Typography variant="body2">{m.name || `#${m.teacherId}`}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">{total}</TableCell>
                      <TableCell align="right">{sameDay}</TableCell>
                      <TableCell align="right">{late}</TableCell>
                      <TableCell align="center">
                        {total > 0 ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress variant="determinate" value={pct} sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: pct >= 80 ? '#4caf50' : pct >= 50 ? '#ff9800' : '#f44336', borderRadius: 3 } }} />
                            <Typography variant="caption" sx={{ minWidth: 40 }}>{pct.toFixed(0)}%</Typography>
                          </Box>
                        ) : '—'}
                      </TableCell>
                      <TableCell align="right">{avgMark != null ? avgMark.toFixed(1) : '—'}</TableCell>
                      <TableCell align="right">{Number(m.markCount || 0)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Evaluation Form Dialog ────────────────────────────────────
const EvaluationDialog = ({ open, onClose, mode, formData, setFormData, staff, saving, onSave, periodOptions, termWindows, teacherMetrics }) => {
  const isView = mode === 'view';
  const selectedStaffMember = staff.find(s => String(s.id) === String(formData.staffId));
  const [autoAppliedKey, setAutoAppliedKey] = useState('');

  const suggestPeriodFromDate = useCallback((dateStr) => {
    if (!dateStr) return '';
    const d = String(dateStr).slice(0, 10);
    if (!d) return '';
    const windows = Array.isArray(termWindows) ? termWindows : [];
    const hit = windows.find((w) => w?.startDate && w?.endDate && d >= String(w.startDate).slice(0, 10) && d <= String(w.endDate).slice(0, 10));
    return hit?.period || '';
  }, [termWindows]);

  const overallRating = useMemo(() => {
    const scores = EVALUATION_CATEGORIES.map(c => formData[c.key]).filter(v => v > 0);
    return scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
  }, [formData]);

  const autoEvaluation = useMemo(
    () => buildAutoEvaluationData(selectedStaffMember, teacherMetrics),
    [selectedStaffMember, teacherMetrics]
  );

  useEffect(() => {
    if (!open) setAutoAppliedKey('');
  }, [open]);

  useEffect(() => {
    if (!open || isView) return;
    const staffId = String(formData.staffId || '');
    if (!staffId) return;
    const applyKey = `${mode}:${staffId}`;
    if (autoAppliedKey === applyKey) return;

    setFormData((prev) => {
      if (String(prev.staffId || '') !== staffId) return prev;
      const next = { ...prev };
      if (mode === 'create') {
        AUTO_CATEGORY_KEYS.forEach((key) => {
          next[key] = autoEvaluation.values[key] || 0;
        });
      } else {
        AUTO_CATEGORY_KEYS.forEach((key) => {
          if ((Number(prev[key]) || 0) <= 0 && autoEvaluation.values[key]) {
            next[key] = autoEvaluation.values[key];
          }
        });
      }
      return next;
    });

    setAutoAppliedKey(applyKey);
  }, [autoAppliedKey, autoEvaluation.values, formData.staffId, isView, mode, open, setFormData]);

  const handleCategoryChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          {mode === 'create' ? 'New Staff Evaluation' : mode === 'edit' ? 'Edit Evaluation' : 'View Evaluation'}
          {selectedStaffMember && (
            <Typography variant="body2" color="text.secondary">{selectedStaffMember.name} — {selectedStaffMember.role}</Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          {/* Staff Selection & Period */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small" disabled={isView}>
              <InputLabel>Staff Member *</InputLabel>
              <Select value={formData.staffId} label="Staff Member *" onChange={e => setFormData(prev => ({ ...prev, staffId: e.target.value }))}>
                {staff.map(s => <MenuItem key={s.id} value={s.id}>{s.name} ({s.role})</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small" disabled={isView}>
              <InputLabel>Period</InputLabel>
              <Select value={formData.period} label="Period" onChange={e => setFormData(prev => ({ ...prev, period: e.target.value }))}>
                <MenuItem value="">— None —</MenuItem>
                {(periodOptions && periodOptions.length ? periodOptions : PERIODS.map((p) => ({ period: p, label: p }))).map((p) => (
                  <MenuItem key={p.period} value={p.period}>
                    {p.label || p.period}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" type="date" label="Date" InputLabelProps={{ shrink: true }} disabled={isView}
              value={formData.evaluationDate} onChange={e => {
                const nextDate = e.target.value;
                setFormData(prev => {
                  const next = { ...prev, evaluationDate: nextDate };
                  if (!next.period) {
                    const suggestion = suggestPeriodFromDate(nextDate);
                    if (suggestion) next.period = suggestion;
                  }
                  return next;
                });
              }} />
          </Grid>

          {!isView && selectedStaffMember && isTeacherEvaluationRole(selectedStaffMember.role) && (
            <Grid item xs={12}>
              <Alert severity={Object.keys(autoEvaluation.values).length ? 'info' : 'warning'}>
                {Object.keys(autoEvaluation.values).length
                  ? 'Attendance and teaching-performance metrics have auto-filled some teacher evaluation categories. You can still adjust them manually.'
                  : 'Automatic teacher evaluation scores will appear here when attendance or class-mark metrics are available for the selected teacher.'}
              </Alert>
            </Grid>
          )}

          {/* Overall Rating Display */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, bgcolor: overallRating >= 3.5 ? 'success.50' : overallRating >= 2.5 ? 'warning.50' : 'grey.50', border: 1, borderColor: 'divider', textAlign: 'center' }}>
              <Typography variant="overline" color="text.secondary">Overall Rating</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 0.5 }}>
                <Typography variant="h3" fontWeight="bold" color={`${ratingColor(overallRating)}.main`}>{overallRating.toFixed(1)}</Typography>
                <Box>
                  <Rating value={overallRating} readOnly precision={0.1} size="large" />
                  <Typography variant="body2" color="text.secondary">{RATING_LABELS[Math.round(overallRating)] || '—'}</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Category Ratings */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Performance Categories</Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          {EVALUATION_CATEGORIES.map(cat => (
            <Grid item xs={12} sm={6} key={cat.key}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" fontWeight="bold">{cat.label}</Typography>
                  {autoEvaluation.reasons[cat.key] && <Chip label="Auto" size="small" color="info" variant="outlined" />}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{cat.desc}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Rating
                    value={formData[cat.key] || 0}
                    onChange={(_, val) => !isView && handleCategoryChange(cat.key, val || 0)}
                    readOnly={isView}
                    size="medium"
                    precision={0.5}
                  />
                  <Chip
                    label={formData[cat.key] > 0 ? `${formData[cat.key].toFixed(1)} - ${RATING_LABELS[Math.round(formData[cat.key])] || ''}` : 'Not rated'}
                    size="small"
                    color={formData[cat.key] > 0 ? ratingColor(formData[cat.key]) : 'default'}
                    variant={formData[cat.key] > 0 ? 'filled' : 'outlined'}
                  />
                </Box>
                {autoEvaluation.reasons[cat.key] && (
                  <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 1 }}>
                    Auto-filled from system data: {autoEvaluation.reasons[cat.key]}
                  </Typography>
                )}
              </Paper>
            </Grid>
          ))}

          {/* Qualitative Feedback */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1, mb: 1 }}>Qualitative Feedback</Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth multiline rows={3} size="small" label="Strengths" disabled={isView}
              value={formData.strengths} onChange={e => setFormData(prev => ({ ...prev, strengths: e.target.value }))}
              placeholder="Highlight the staff member's key strengths..." />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth multiline rows={3} size="small" label="Areas for Improvement" disabled={isView}
              value={formData.areasForImprovement} onChange={e => setFormData(prev => ({ ...prev, areasForImprovement: e.target.value }))}
              placeholder="Identify areas that need development..." />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth multiline rows={3} size="small" label="Goals & Objectives" disabled={isView}
              value={formData.goals} onChange={e => setFormData(prev => ({ ...prev, goals: e.target.value }))}
              placeholder="Set goals for the next evaluation period..." />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth multiline rows={3} size="small" label="Additional Comments" disabled={isView}
              value={formData.comments} onChange={e => setFormData(prev => ({ ...prev, comments: e.target.value }))}
              placeholder="Any additional observations or notes..." />
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          {isView ? 'Close' : 'Cancel'}
        </Button>
        {!isView && (
          <>
            <Button onClick={() => onSave('draft')} disabled={saving} variant="outlined" startIcon={<Save />}>
              Save Draft
            </Button>
            <Button onClick={() => onSave('submitted')} disabled={saving} variant="contained" color="primary" startIcon={saving ? <CircularProgress size={18} /> : <CheckCircle />}>
              Submit Evaluation
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default StaffEvaluationPage;
